/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  BulkPublishResponse,
  DeleteAdminV1StoresStoreIdMarketsMarketIdPricesVariantIdPathParameters,
  DeleteAdminV1StoresStoreIdProductsIdPathParameters,
  GetAdminV1StoresStoreIdMarketsMarketIdPrices200,
  GetAdminV1StoresStoreIdMarketsMarketIdPricesParams,
  GetAdminV1StoresStoreIdMarketsMarketIdPricesPathParameters,
  GetAdminV1StoresStoreIdProductsIdPathParameters,
  GetAdminV1StoresStoreIdProductsIdPricing200,
  GetAdminV1StoresStoreIdProductsIdPricingPathParameters,
  GetAdminV1StoresStoreIdProductsParams,
  GetAdminV1StoresStoreIdProductsPathParameters,
  GetAdminV1StoresStoreIdSearchReindexPathParameters,
  GetAdminV1StoresStoreIdVariantsVariantIdPricingPathParameters,
  PatchAdminV1StoresStoreIdProductsIdBody,
  PatchAdminV1StoresStoreIdProductsIdPathParameters,
  PostAdminV1StoresStoreIdProductsBody,
  PostAdminV1StoresStoreIdProductsLookupPathParameters,
  PostAdminV1StoresStoreIdProductsPathParameters,
  PostAdminV1StoresStoreIdSearchReindexPathParameters,
  ProductLookupRequest,
  ProductLookupResponse,
  PutAdminV1StoresStoreIdMarketsMarketIdPrices200,
  PutAdminV1StoresStoreIdMarketsMarketIdPricesBody,
  PutAdminV1StoresStoreIdMarketsMarketIdPricesPathParameters,
  PutAdminV1StoresStoreIdProductsBulkPublishBody,
  PutAdminV1StoresStoreIdProductsBulkPublishPathParameters,
  PutAdminV1StoresStoreIdVariantsPricingBulk200,
  PutAdminV1StoresStoreIdVariantsPricingBulkBody,
  PutAdminV1StoresStoreIdVariantsPricingBulkPathParameters,
  PutAdminV1StoresStoreIdVariantsVariantIdPricingBody,
  PutAdminV1StoresStoreIdVariantsVariantIdPricingPathParameters,
  ReindexResponse,
  ReindexState,
  StoreProduct,
  StoreProductListPage,
  StoreVariantPricing
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * Each row carries the rotation that owns it (schedule, null when none); scheduleId and scheduleState keep only the rows a schedule owns. Store-only filters: `stock` (available = on hand minus reserved over the store's locations for active tracked variants; an active untracked variant counts as in stock; `low_stock` when any level is at or below its reorder point) and `unpriced` (as the store's default market resolves prices). Filters (D-182) combine with AND, values of one filter with OR. `attr.<key>=a,b` filters a filterable select, multiselect or boolean attribute by value code (or true/false); an unknown or non-filterable key answers 422 on query.attr.<key>. `includeTotal=true` on the first page adds `total` (at most 10,000) and `totalCapped`. `memberOf=store:<id>|collection:<id>|schedule:<id>` adds `isMember` and `memberReason` to each row; a target the caller cannot see answers 404 on query.memberOf. Sorts take the id as tie-break; `price` is the lowest active variant's base price, products without one last. A filter of the other list's scope answers 422
 * @summary Products in the store, newest publication first
 */
export const getAdminV1StoresStoreIdProducts = (
    { storeId }: GetAdminV1StoresStoreIdProductsPathParameters,
    params?: GetAdminV1StoresStoreIdProductsParams,
 options?: SecondParameter<typeof customInstance<StoreProductListPage>>,) => {
      return customInstance<StoreProductListPage>(
      {url: `/admin/v1/stores/${storeId}/products`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Creates the publication with optional overrides. status published needs an active master product (409 otherwise). The store handle defaults to the product handle and must be unique in the store
 * @summary Add a master product to the store
 */
export const postAdminV1StoresStoreIdProducts = (
    { storeId }: PostAdminV1StoresStoreIdProductsPathParameters,
    postAdminV1StoresStoreIdProductsBody: BodyType<PostAdminV1StoresStoreIdProductsBody>,
 options?: SecondParameter<typeof customInstance<StoreProduct>>,) => {
      return customInstance<StoreProduct>(
      {url: `/admin/v1/stores/${storeId}/products`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdProductsBody
    },
      options);
    }
  /**
 * Exact and case-insensitive, up to 5,000 values. `auto` tries a product or variant id, then a variant SKU, then a barcode, then a handle, and keeps the first kind that matched each value. Deleted products and variants never match. Read-only; the picker adds the matches to its selection and lists notFound. Only products in this store match; handles match the store handle as well as the master one
 * @summary Find the store's products by pasted ids, SKUs, barcodes or handles
 */
export const postAdminV1StoresStoreIdProductsLookup = (
    { storeId }: PostAdminV1StoresStoreIdProductsLookupPathParameters,
    productLookupRequest: BodyType<ProductLookupRequest>,
 options?: SecondParameter<typeof customInstance<ProductLookupResponse>>,) => {
      return customInstance<ProductLookupResponse>(
      {url: `/admin/v1/stores/${storeId}/products/lookup`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: productLookupRequest
    },
      options);
    }
  /**
 * Missing publications are created with the product handle. Products that cannot be published are skipped with a reason. Takes productIds (at most 5,000) or a selection in org scope (from the master catalog) or store scope (this store's catalog), not both (422 INVALID_INPUT). A selection resolves inside the call: 409 SELECTION_CHANGED (meta.expected, meta.actual) when its filter no longer counts expectedCount, 422 SELECTION_TOO_LARGE (meta.limit) past 5,000 products
 * @summary Publish, unpublish or draft many products at once
 */
export const putAdminV1StoresStoreIdProductsBulkPublish = (
    { storeId }: PutAdminV1StoresStoreIdProductsBulkPublishPathParameters,
    putAdminV1StoresStoreIdProductsBulkPublishBody: BodyType<PutAdminV1StoresStoreIdProductsBulkPublishBody>,
 options?: SecondParameter<typeof customInstance<BulkPublishResponse>>,) => {
      return customInstance<BulkPublishResponse>(
      {url: `/admin/v1/stores/${storeId}/products/bulk-publish`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: putAdminV1StoresStoreIdProductsBulkPublishBody
    },
      options);
    }
  /**
 * @summary Get a store product
 */
export const getAdminV1StoresStoreIdProductsId = (
    { storeId, id }: GetAdminV1StoresStoreIdProductsIdPathParameters,
 options?: SecondParameter<typeof customInstance<StoreProduct>>,) => {
      return customInstance<StoreProduct>(
      {url: `/admin/v1/stores/${storeId}/products/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * Null clears an override so the master value shows again; seo merges per key with what is stored. publishedAt is set the first time the status becomes published. publishAt / unpublishAt (future instants) are fired by the worker every minute and cleared once applied; the status check against the master product runs only when status actually changes
 * @summary Change overrides, status or schedule
 */
export const patchAdminV1StoresStoreIdProductsId = (
    { storeId, id }: PatchAdminV1StoresStoreIdProductsIdPathParameters,
    patchAdminV1StoresStoreIdProductsIdBody: BodyType<PatchAdminV1StoresStoreIdProductsIdBody>,
 options?: SecondParameter<typeof customInstance<StoreProduct>>,) => {
      return customInstance<StoreProduct>(
      {url: `/admin/v1/stores/${storeId}/products/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdProductsIdBody
    },
      options);
    }
  /**
 * Store-level overrides and the product's memberships in this store's collections go with it; master product and store variant prices stay
 * @summary Remove the product from the store
 */
export const deleteAdminV1StoresStoreIdProductsId = (
    { storeId, id }: DeleteAdminV1StoresStoreIdProductsIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/products/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * effective follows doc 04: price list entry, then store override, then the base price converted to the default market currency
 * @summary Every variant of the product with its price in this store
 */
export const getAdminV1StoresStoreIdProductsIdPricing = (
    { storeId, id }: GetAdminV1StoresStoreIdProductsIdPricingPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdProductsIdPricing200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdProductsIdPricing200>(
      {url: `/admin/v1/stores/${storeId}/products/${id}/pricing`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Price of one variant in this store
 */
export const getAdminV1StoresStoreIdVariantsVariantIdPricing = (
    { storeId, variantId }: GetAdminV1StoresStoreIdVariantsVariantIdPricingPathParameters,
 options?: SecondParameter<typeof customInstance<StoreVariantPricing>>,) => {
      return customInstance<StoreVariantPricing>(
      {url: `/admin/v1/stores/${storeId}/variants/${variantId}/pricing`, method: 'GET'
    },
      options);
    }
  /**
 * Prices are in the store currency. price null removes the override and the base price applies again
 * @summary Set the store price, availability and SKU override of a variant
 */
export const putAdminV1StoresStoreIdVariantsVariantIdPricing = (
    { storeId, variantId }: PutAdminV1StoresStoreIdVariantsVariantIdPricingPathParameters,
    putAdminV1StoresStoreIdVariantsVariantIdPricingBody: BodyType<PutAdminV1StoresStoreIdVariantsVariantIdPricingBody>,
 options?: SecondParameter<typeof customInstance<StoreVariantPricing>>,) => {
      return customInstance<StoreVariantPricing>(
      {url: `/admin/v1/stores/${storeId}/variants/${variantId}/pricing`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: putAdminV1StoresStoreIdVariantsVariantIdPricingBody
    },
      options);
    }
  /**
 * @summary Set store prices for many variants in one call
 */
export const putAdminV1StoresStoreIdVariantsPricingBulk = (
    { storeId }: PutAdminV1StoresStoreIdVariantsPricingBulkPathParameters,
    putAdminV1StoresStoreIdVariantsPricingBulkBody: BodyType<PutAdminV1StoresStoreIdVariantsPricingBulkBody>,
 options?: SecondParameter<typeof customInstance<PutAdminV1StoresStoreIdVariantsPricingBulk200>>,) => {
      return customInstance<PutAdminV1StoresStoreIdVariantsPricingBulk200>(
      {url: `/admin/v1/stores/${storeId}/variants/pricing/bulk`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: putAdminV1StoresStoreIdVariantsPricingBulkBody
    },
      options);
    }
  /**
 * Doc 04: a fixed price wins over the store override and the converted base. Empty when the market has no price list yet
 * @summary Fixed per-market variant prices (the market's price list)
 */
export const getAdminV1StoresStoreIdMarketsMarketIdPrices = (
    { storeId, marketId }: GetAdminV1StoresStoreIdMarketsMarketIdPricesPathParameters,
    params?: GetAdminV1StoresStoreIdMarketsMarketIdPricesParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdMarketsMarketIdPrices200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdMarketsMarketIdPrices200>(
      {url: `/admin/v1/stores/${storeId}/markets/${marketId}/prices`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Upserts per variant in the market currency; the first call creates the market's price list. Variants not listed keep their entries
 * @summary Set fixed prices for variants in a market
 */
export const putAdminV1StoresStoreIdMarketsMarketIdPrices = (
    { storeId, marketId }: PutAdminV1StoresStoreIdMarketsMarketIdPricesPathParameters,
    putAdminV1StoresStoreIdMarketsMarketIdPricesBody: BodyType<PutAdminV1StoresStoreIdMarketsMarketIdPricesBody>,
 options?: SecondParameter<typeof customInstance<PutAdminV1StoresStoreIdMarketsMarketIdPrices200>>,) => {
      return customInstance<PutAdminV1StoresStoreIdMarketsMarketIdPrices200>(
      {url: `/admin/v1/stores/${storeId}/markets/${marketId}/prices`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: putAdminV1StoresStoreIdMarketsMarketIdPricesBody
    },
      options);
    }
  /**
 * The store override or the converted base price applies again
 * @summary Remove a variant's fixed price from a market
 */
export const deleteAdminV1StoresStoreIdMarketsMarketIdPricesVariantId = (
    { storeId, marketId, variantId }: DeleteAdminV1StoresStoreIdMarketsMarketIdPricesVariantIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/markets/${marketId}/prices/${variantId}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Normally unnecessary: publication, pricing, product and collection changes queue a reindex through the outbox. Requests within two seconds share one job; follow it with GET /search/reindex
 * @summary Rebuild the store's search index on the worker
 */
export const postAdminV1StoresStoreIdSearchReindex = (
    { storeId }: PostAdminV1StoresStoreIdSearchReindexPathParameters,
 options?: SecondParameter<typeof customInstance<ReindexResponse>>,) => {
      return customInstance<ReindexResponse>(
      {url: `/admin/v1/stores/${storeId}/search/reindex`, method: 'POST'
    },
      options);
    }
  /**
 * Read from the worker queue: queued, running, completed (with the document count) or failed (with the reason). 404 while no rebuild was ever queued or the queue no longer keeps it
 * @summary State of the store's most recent index rebuild
 */
export const getAdminV1StoresStoreIdSearchReindex = (
    { storeId }: GetAdminV1StoresStoreIdSearchReindexPathParameters,
 options?: SecondParameter<typeof customInstance<ReindexState>>,) => {
      return customInstance<ReindexState>(
      {url: `/admin/v1/stores/${storeId}/search/reindex`, method: 'GET'
    },
      options);
    }
  export type GetAdminV1StoresStoreIdProductsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdProducts>>>
export type PostAdminV1StoresStoreIdProductsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdProducts>>>
export type PostAdminV1StoresStoreIdProductsLookupResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdProductsLookup>>>
export type PutAdminV1StoresStoreIdProductsBulkPublishResult = NonNullable<Awaited<ReturnType<typeof putAdminV1StoresStoreIdProductsBulkPublish>>>
export type GetAdminV1StoresStoreIdProductsIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdProductsId>>>
export type PatchAdminV1StoresStoreIdProductsIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdProductsId>>>
export type DeleteAdminV1StoresStoreIdProductsIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdProductsId>>>
export type GetAdminV1StoresStoreIdProductsIdPricingResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdProductsIdPricing>>>
export type GetAdminV1StoresStoreIdVariantsVariantIdPricingResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdVariantsVariantIdPricing>>>
export type PutAdminV1StoresStoreIdVariantsVariantIdPricingResult = NonNullable<Awaited<ReturnType<typeof putAdminV1StoresStoreIdVariantsVariantIdPricing>>>
export type PutAdminV1StoresStoreIdVariantsPricingBulkResult = NonNullable<Awaited<ReturnType<typeof putAdminV1StoresStoreIdVariantsPricingBulk>>>
export type GetAdminV1StoresStoreIdMarketsMarketIdPricesResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdMarketsMarketIdPrices>>>
export type PutAdminV1StoresStoreIdMarketsMarketIdPricesResult = NonNullable<Awaited<ReturnType<typeof putAdminV1StoresStoreIdMarketsMarketIdPrices>>>
export type DeleteAdminV1StoresStoreIdMarketsMarketIdPricesVariantIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdMarketsMarketIdPricesVariantId>>>
export type PostAdminV1StoresStoreIdSearchReindexResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdSearchReindex>>>
export type GetAdminV1StoresStoreIdSearchReindexResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdSearchReindex>>>
